import firebase from "@/firebase"
import Idea from "@/types/Idea"
import {isMediaType} from "@/utils/mediaTypes"

/**
 * Único módulo que fala com o Firebase Storage, mais o registro dos arquivos
 * escolhidos e ainda não salvos.
 *
 * O valor de uma propriedade de mídia é sempre uma URL curta: https:// quando já
 * persistida, blob: enquanto pendente. O arquivo de verdade fica aqui, fora do
 * objeto reativo — antes a data URL inteira morava na instância, era copiada a cada
 * deepCopy e comparada a cada render pelo diff que habilita o botão salvar.
 *
 * O registro é singleton de módulo de propósito: o campo pode ser destruído entre
 * escolher e salvar (o v-for do InstanceInput filtra por invisible), e o
 * EditInstanceService é construído de novo em cada tela. É um registro só para
 * imagem e áudio — dividir em dois apenas duplicaria bug.
 */
const pending = new Map<string, Blob>()

/** Uma propriedade de mídia de uma coleção: o alias e o que ele guarda. */
export type MediaRef = { alias: string, type: string }

export function isPending(value: any): value is string {
    return typeof value === 'string' && value.startsWith('blob:')
}

/** Guarda o Blob e devolve a URL que representa o arquivo no formulário. */
export function registerPending(blob: Blob): string {
    const url = URL.createObjectURL(blob)
    pending.set(url, blob)
    return url
}

export function takePending(url: string): Blob | undefined {
    return pending.get(url)
}

/**
 * Só quem sabe que ninguém mais aponta para a URL pode revogar: depois de o modelo
 * ter sido reescrito com a URL definitiva, ou quando outro Blob substitui este no
 * mesmo campo. O componente nunca revoga — ele é destruído e remontado.
 */
export function revoke(url: any) {
    if (!isPending(url)) return
    if (pending.delete(url)) URL.revokeObjectURL(url)
}

export async function upload(path: string, blob: Blob): Promise<string> {
    const ref = firebase.storage().ref(path)
    //contentType explícito: o caminho não tem extensão, então é o metadado que diz
    //ao navegador o que ele está baixando. Para áudio isso é crítico — o blob é o
    //arquivo cru e o File.type do sistema operacional costuma vir vazio, por isso o
    //prepare de áudio recarimba o MIME antes de chegar aqui.
    await ref.put(blob, {contentType: blob.type})
    return await ref.getDownloadURL()
}

export async function deleteAt(path: string): Promise<void> {
    try {
        await firebase.storage().ref(path).delete()
    } catch (err) {
        //apagar o que não existe já é o estado desejado
        if (err && err.code === 'storage/object-not-found') return
        throw err
    }
}

/** Pasta raiz por tipo. `images/` é legado e não pode mudar: há arquivos lá. */
const PREFIXES: { [type: string]: string } = {
    image: 'images',
    audio: 'audio'
}

/**
 * Caminho do arquivo no bucket. Determinístico de propósito: reenviar sobrescreve,
 * então trocar a mídia nunca acumula órfão e não há o que limpar depois.
 *
 * Mora aqui, e não no EditInstanceService que é quem mais o usa, porque o
 * reverseRelationship também precisa dele — e a volta criaria import circular.
 */
export function instanceMediaPath(
    systemAlias: string, ideaAlias: string, instanceId: number | string,
    alias: string, type: string
): string {
    const prefix = PREFIXES[type] || type
    return `${prefix}/${systemAlias}/${ideaAlias}/${String(instanceId)}/${alias}`
}

/**
 * Caminho da foto de uma pessoa, pelo uid dela.
 *
 * Determinístico como o da instância, e pela mesma razão: trocar a foto sobrescreve, e nunca
 * sobra órfão para limpar depois.
 *
 * Pelo **uid** e não pelo e-mail: o e-mail é a identidade do lunedi, mas quem nomeia arquivo é o
 * dono da conta no Firebase — e o uid não tem `@`, ponto nem `+` para escapar num caminho.
 */
export function userPhotoPath(uid: string): string {
    return `${PREFIXES.image}/users/${uid}/photo`
}

export function mediaAliasesOf(idea: Idea): MediaRef[] {
    return idea.properties
        .filter(p => isMediaType(p))
        .map(p => ({alias: p.alias, type: p.type as string}))
}

/** Apaga as mídias de uma instância. Best-effort: falhar aqui não impede excluir. */
export async function deleteInstanceMedia(
    systemAlias: string, ideaAlias: string, instanceId: number | string, idea: Idea
): Promise<void> {
    await Promise.all(mediaAliasesOf(idea).map(async ({alias, type}) => {
        try {
            await deleteAt(instanceMediaPath(systemAlias, ideaAlias, instanceId, alias, type))
        } catch (err) {
            //arquivo sobrando no bucket é menos grave do que a exclusão falhar
        }
    }))
}

export default {
    isPending, registerPending, takePending, revoke, upload, deleteAt,
    instanceMediaPath, mediaAliasesOf, deleteInstanceMedia
}
